import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useStripe, useElements, IbanElement } from '@stripe/react-stripe-js';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ButtonGroup from '../ButtonGroup';
import {
  Input,
  Loader,
  MapBoxAutocompleteInput,
  PrimaryButton,
  SecondaryButton,
} from 'common/components';
import { useBillingInfo } from 'features/feature-clinic-registration/hooks/useBillingInfo';
import { useBillingInfoSchema } from 'features/feature-clinic-registration/hooks/useBillingInfoSchema';
import {
  BilligInfoStyled,
  IBAN_STYLE,
  IbanSection,
  IbanStyled,
} from './styles';
import {
  currentClinicOwnerRegistration,
  getIsBackModalShowSessionStorage,
  setClinicOwnerRegistrationSessionStorage,
} from 'applicaiton/sessionStorage/clinicRegistration';
import { Context } from 'common/types/mapBox';
import { SetupIntent } from 'common/types/stripe';
import BackModal from '../BackModal';
import { useAppDispatch } from 'common/hooks/redux';
import { clearClinicOwnerSuccessSignUp } from 'applicaiton/store/reducers/ClinicRegistration/ClinicRegistrationSlice';
import { Location } from './modules';
import { getClientSecret } from 'applicaiton/store/reducers/ClinicRegistration/ActionCreators';

interface BillingInfoProps {
  setStep: (value: number) => void;
}

interface FormData {
  companyName: string;
  iban: string;
}

const BillingInfo: FC<BillingInfoProps> = ({ setStep }) => {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useAppDispatch();
  const { companyName, iban, address } = useBillingInfo();
  const [location, setLocation] = useState<Location>(address);
  const [sepaClientSecret, setSepaClientSecret] = useState('');
  const { personalInfo, stripeDebit } = currentClinicOwnerRegistration();
  const [openModal, setOpenModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const billingInfoSchema = useBillingInfoSchema();

  const {
    register,
    getValues,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(billingInfoSchema),
    defaultValues: { companyName, iban },
  });

  const handleSetAddress = (context: Context) => {
    setLocation({
      city: context.place.name,
      country: context.country.country_code,
      postal_code: context.postcode.name,
      line1: context.address?.name || context.street?.name || '',
    });
  };

  useEffect(() => {
    stripeDebit?.token &&
      dispatch(getClientSecret(stripeDebit.token)).then((data) => {
        if (data.meta.requestStatus === 'fulfilled') {
          setSepaClientSecret(data.payload.sepaClientSecret);
          setClinicOwnerRegistrationSessionStorage('stripeDebit', {
            ...data.payload,
            token: stripeDebit.token,
          });
        }
      });
  }, []);

  useEffect(() => {
    const values = getValues();
    setClinicOwnerRegistrationSessionStorage('billingInfo', {
      ...values,
      address: location,
    });
  }, [watch('companyName'), location]);

  const IBAN_ELEMENT_OPTIONS = {
    supportedCountries: ['SEPA'],
    // Elements can use a placeholder as an example IBAN that reflects
    // the IBAN format of your customer's country. If you know your
    // customer's country, we recommend that you pass it to the Element as the
    // placeholderCountry.
    placeholderCountry: 'PT',
    style: IBAN_STYLE,
  };

  const handleSubmit = async () => {
    const ibanElement = elements?.getElement(IbanElement);
    if (!stripe || !elements || !ibanElement) {
      return;
    }
    setIsLoading(true);
    const { setupIntent } = await stripe.confirmSepaDebitSetup(
      sepaClientSecret,
      {
        payment_method: {
          sepa_debit: ibanElement,
          billing_details: {
            name: getValues('companyName'),
            email: personalInfo.email!,
            address: location,
            phone: personalInfo.phone,
          },
        },
      },
    );
    setIsLoading(false);
    if (setupIntent) {
      setClinicOwnerRegistrationSessionStorage(
        'setupIntent',
        (setupIntent || {}) as SetupIntent,
      );
      setStep(5);
    }
  };

  const getAddressStreet = () => {
    if (location) {
      const address = [
        location.line1 || '',
        location.postal_code || '',
        location.city || '',
        location.country || '',
      ]
        .filter((item) => !!item)
        .join(' ');
      return address;
    }
    return '';
  };
  const disabledButton = !location || !getValues('companyName');

  const handleBackButton = () => {
    if (getIsBackModalShowSessionStorage() === 'N') {
      setOpenModal(true);
    } else {
      dispatch(clearClinicOwnerSuccessSignUp());
      setStep(2);
    }
  };

  return (
    <>
      {isLoading && <Loader />}
      <BilligInfoStyled>
        <section>
          <h2>
            {t('clinicRegistration.billing_information.personal_info_SEPA')}
          </h2>
          <div>
            <Input
              id="companyName"
              label={
                t('clinicRegistration.billing_information.company_name') || ''
              }
              type="text"
              placeholder={
                t(
                  'clinicRegistration.billing_information.company_name_placeholder',
                ) || ''
              }
              errorMessage={errors.companyName?.message}
              register={register}
            />
            <MapBoxAutocompleteInput
              id={'address'}
              label={t('clinicRegistration.billing_information.address') || ''}
              defaultValue={getAddressStreet()}
              setLocation={handleSetAddress}
              countryIso={personalInfo?.supportedCountry?.code}
            />
            <IbanSection>
              <label>IBAN</label>
              <IbanStyled>
                <IbanElement options={IBAN_ELEMENT_OPTIONS} />
              </IbanStyled>
            </IbanSection>
          </div>
        </section>
        <ButtonGroup>
          <PrimaryButton
            disabled={disabledButton}
            type="button"
            onClick={handleSubmit}
          >
            {t('clinicRegistration.continue')}
          </PrimaryButton>
          <SecondaryButton
            onClick={handleBackButton}
            disabled={false}
            type="button"
          >
            {t('clinicRegistration.back')}
          </SecondaryButton>
        </ButtonGroup>
      </BilligInfoStyled>
      <BackModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        backToStep={() => setStep(2)}
      />
    </>
  );
};

export default BillingInfo;
